import React, {useEffect, useMemo, useState} from 'react';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import {EdgePlan} from 'app/models/EdgePlan/EdgePlan';
import {StyleSx} from 'app/types/common';
import {TabPanels} from 'app/components/sharedReactComponents/Tabs/TabPanels';
import {TabPanel} from 'app/components/sharedReactComponents/Tabs/TabPanel';
import {ConnectUsageAndInvoices} from 'app/components/Settings/BillingAndUsage/UsageAndInvoices/ConnectUsageAndInvoices/ConnectUsageAndInvoices';
import {useFeature} from 'app/components/entities/cloud';
import {ConnectBilling} from 'app/components/entities/billing/connect';
import {EdgeUsage, FilesUsage, PayAsYouGoUsage} from 'app/components/widgets/billing';

const tabsSx: StyleSx = {
  mb: 3,
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: (t) => t.palette.divider,
};
const tabSx: StyleSx = {fontSize: 16};
const bodySx: StyleSx = {minHeight: 300};

interface Props {
  teamId: string;
  cloud: EdgePlan;
  connect?: ConnectBilling.RegularPlan;
}

type UsageTab = 'edge' | 'pay-as-you-go' | 'connect' | 'files';

export function UsageAndInvoices({teamId, cloud, connect}: Props) {
  const [tab, setTab] = useState<UsageTab>('edge');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue as UsageTab);
  };

  const unifyFeature = useFeature('unify');
  const connectFeature = useFeature('connect');
  const filesFeature = useFeature('files');

  const isLegacyEdge = /^[pt]/.test(cloud.tier.id);

  const tabs = useMemo<UsageTab[]>(() => {
    const result: UsageTab[] = ['edge'];

    if (!isLegacyEdge) {
      result.push('pay-as-you-go');
    }

    if (connectFeature) {
      result.push('connect');
    }

    if (filesFeature) {
      result.push('files');
    }

    return result;
  }, [isLegacyEdge, connectFeature, filesFeature]);

  useEffect(() => {
    if (!tabs.includes(tab)) {
      setTab('edge');
    }
  }, [tabs, tab]);

  return (
    <Box>
      <Typography fontWeight={600} variant="h5" mb={3}>
        Usage and invoices
      </Typography>

      <Tabs sx={tabsSx} value={tab} textColor="inherit" onChange={handleTabChange}>
        <Tab
          sx={tabSx}
          data-id="edge-usage-tab"
          value="edge"
          label={isLegacyEdge && unifyFeature ? 'Edge & Unify' : 'Edge'}
        />

        {!isLegacyEdge && (
          <Tab
            sx={tabSx}
            data-id="pay-as-you-go-usage-tab"
            value="pay-as-you-go"
            label={unifyFeature ? 'LiveScript & Unify' : 'LiveScript'}
          />
        )}

        {connectFeature && (
          <Tab sx={tabSx} data-id="connect-usage-tab" value="connect" label="Connect" />
        )}

        {filesFeature && (
          <Tab sx={tabSx} data-id="files-usage-tab" value="files" label="File management" />
        )}
      </Tabs>

      <TabPanels value={tab}>
        <TabPanel value="edge">
          {isLegacyEdge ? (
            <PayAsYouGoUsage
              sx={bodySx}
              teamId={teamId}
              period={cloud.period}
              showUnify={unifyFeature}
            />
          ) : (
            <EdgeUsage sx={bodySx} teamId={teamId} />
          )}
        </TabPanel>

        {!isLegacyEdge && (
          <TabPanel value="pay-as-you-go">
            <PayAsYouGoUsage
              sx={bodySx}
              teamId={teamId}
              period={cloud.period}
              showUnify={unifyFeature}
            />
          </TabPanel>
        )}

        {connectFeature && (
          <TabPanel value="connect">
            <ConnectUsageAndInvoices sx={bodySx} subscription={connect} teamId={teamId} />
          </TabPanel>
        )}

        {filesFeature && (
          <TabPanel value="files">
            <FilesUsage sx={bodySx} teamId={teamId} />
          </TabPanel>
        )}
      </TabPanels>
    </Box>
  );
}
